"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const featuredProducts = [
{
  id: "FP-2026-0041",
  name: "Fort Jesus Museum",
  category: "Cultural Heritage",
  county: "Mombasa County",
  image: "https://images.unsplash.com/photo-1691071100982-b7a71eec3575",
  alt: "Historic Fort Jesus stone walls and Portuguese architecture at the Mombasa waterfront Kenya",
  status: "Approved",
  rating: 4.9,
  submittedBy: "Mombasa County Tourism Office",
  approvedDate: "Feb 12, 2026"
},
{
  id: "FP-2026-0038",
  name: "Lake Turkana Cultural Festival",
  category: "Cultural Heritage",
  county: "Turkana County",
  image: "https://images.unsplash.com/photo-1677254113814-9fbcca59dddb",
  alt: "Colorful traditional tribal gathering ceremony at Lake Turkana shores in northern Kenya",
  status: "Approved",
  rating: 4.8,
  submittedBy: "Turkana County Government",
  approvedDate: "Feb 8, 2026"
},
{
  id: "FP-2026-0035",
  name: "Kakamega Forest Eco-Lodge",
  category: "Eco-Tourism",
  county: "Kakamega County",
  image: "https://images.unsplash.com/photo-1531060140932-2d031106bdf9",
  alt: "Lush tropical rainforest canopy at Kakamega Forest Reserve Kenya with morning mist",
  status: "Approved",
  rating: 4.7,
  submittedBy: "Kakamega Forest Tourism CBO",
  approvedDate: "Jan 30, 2026"
}];


const categoryColors: Record<string, string> = {
  "Cultural Heritage": "kenya-red",
  "Eco-Tourism": "kenya-green",
  "Wildlife": "kenya-black"
};

export default function FeaturedProductsSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll(".feat-item");
            items.forEach((item, i) => {
              setTimeout(() => item.classList.add("revealed"), i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-warm-bg">
      <div className="max-w-8xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 sm:gap-8 mb-10 sm:mb-16 feat-item reveal-hidden">
          <div className="space-y-4 sm:space-y-5">
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-kenya-green/10 border border-kenya-green/20">
              <div className="w-2 h-2 rounded-full bg-kenya-green pulse-dot" />
              <span className="text-[0.6875rem] font-bold uppercase tracking-[0.4em] text-kenya-green font-condensed">
                Recently Approved
              </span>
            </div>
            <h2
              className="font-display font-bold text-kenya-black"
              style={{ fontSize: "clamp(1.75rem, 4vw, 3.5rem)", lineHeight: "0.92", letterSpacing: "-0.025em" }}
            >
              Featured Tourism
              <br />
              <span className="font-display italic font-light text-kenya-red">Products</span>
            </h2>
          </div>
          <p className="text-text-muted max-w-xs text-[0.875rem] sm:text-[0.9375rem] leading-relaxed">
            Verified and approved products now featured in national and international marketing channels.
          </p>
        </div>

        {/* Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {featuredProducts.map((product, idx) =>
          <div
            key={product.id}
            className="feat-item reveal-hidden product-card bg-white rounded-xl2 overflow-hidden border-2 border-kenya-black/8 cursor-pointer"
            style={{ transitionDelay: `${idx * 100}ms` }}>
            
              {/* Image */}
              <div className="relative aspect-[4/3] overflow-hidden">
                <AppImage
                src={product.image}
                alt={product.alt}
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-700"
                sizes="(max-width: 768px) 100vw, 33vw" />
              
                {/* Status badge */}
                <div className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-kenya-green/90 backdrop-blur-sm">
                  <Icon name="CheckBadgeIcon" size={13} className="text-white" />
                  <span className="text-[0.6875rem] font-bold uppercase tracking-[0.25em] text-white font-condensed">
                    {product.status}
                  </span>
                </div>
                {/* ID */}
                <div className="absolute top-4 right-4 px-2.5 py-1.5 rounded bg-kenya-black/65 backdrop-blur-sm">
                  <span className="text-[0.6875rem] font-mono text-white/75">{product.id}</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div
                    className={`inline-block text-[0.6875rem] font-bold uppercase tracking-[0.25em] px-2.5 py-1 rounded mb-2.5 font-condensed ${
                    categoryColors[product.category] === "kenya-red" ? "bg-kenya-red/10 text-kenya-red" : "bg-kenya-green/10 text-kenya-green"}`
                    }>
                    
                      {product.category}
                    </div>
                    <h3 className="font-bold text-kenya-black text-xl leading-tight font-display">
                      {product.name}
                    </h3>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Icon name="StarIcon" size={15} className="text-amber-400" variant="solid" />
                    <span className="text-[0.9375rem] font-bold text-kenya-black">{product.rating}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-text-muted text-[0.875rem]">
                  <Icon name="MapPinIcon" size={13} />
                  {product.county}
                </div>

                <div className="pt-4 border-t-2 border-kenya-black/8 flex items-center justify-between">
                  <div>
                    <div className="text-[0.6875rem] text-text-muted uppercase tracking-widest font-condensed mb-0.5">Submitted by</div>
                    <div className="text-[0.875rem] font-semibold text-kenya-black">{product.submittedBy}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[0.6875rem] text-text-muted uppercase tracking-widest font-condensed mb-0.5">Approved</div>
                    <div className="text-[0.875rem] font-semibold text-kenya-green">{product.approvedDate}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Marquee */}
        <div className="mt-12 sm:mt-20 overflow-hidden border-y-2 border-kenya-black/8 py-4 sm:py-6">
          <div className="marquee-content flex items-center gap-16">
            {[
            "Maasai Mara · Narok",
            "Fort Jesus · Mombasa",
            "Lake Turkana · Turkana",
            "Diani Beach · Kwale",
            "Amboseli · Kajiado",
            "Kakamega Forest · Kakamega",
            "Rusinga Island · Homa Bay",
            "Lamu Old Town · Lamu",
            "Mount Kenya · Nyeri",
            "Hell's Gate · Nakuru",
            "Maasai Mara · Narok",
            "Fort Jesus · Mombasa",
            "Lake Turkana · Turkana",
            "Diani Beach · Kwale",
            "Amboseli · Kajiado",
            "Kakamega Forest · Kakamega",
            "Rusinga Island · Homa Bay",
            "Lamu Old Town · Lamu",
            "Mount Kenya · Nyeri",
            "Hell's Gate · Nakuru"].
            map((item, i) =>
            <span
              key={i}
              className={`text-[0.875rem] font-bold uppercase tracking-[0.3em] whitespace-nowrap font-condensed ${
              i % 3 === 0 ?
              "text-kenya-red/40" :
              i % 3 === 1 ?
              "text-kenya-black/25" : "text-kenya-green/40"}`
              }>
              
                {item}
              </span>
            )}
          </div>
        </div>
      </div>
    </section>);

}